import { useCallback, useEffect, useState } from 'react';
import type { ExportProgressDTO, ExportResultDTO } from '../../global';
import { plural, t } from '../../lib/i18n';

/**
 * Arşiv dışa aktarımı: klasör seçtirir, ilerlemeyi izler, sonucu tutar.
 * `companyId` verilirse yalnızca o şirketin toplantıları aktarılır.
 */
export function useArchiveExport(onNotify?: (kind: 'success' | 'error' | 'info', text: string) => void) {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<ExportProgressDTO | null>(null);
  const [result, setResult] = useState<Extract<ExportResultDTO, { ok: true }> | null>(null);

  useEffect(() => {
    if (!isRunning) return;
    return window.zabit.onExportProgress(setProgress);
  }, [isRunning]);

  const run = useCallback(
    async (companyId?: string) => {
      setIsRunning(true);
      setProgress(null);
      setResult(null);
      try {
        const res = await window.zabit.exportArchive(companyId);
        if (res.ok) {
          setResult(res);
          onNotify?.(
            res.failed > 0 ? 'error' : 'success',
            res.failed > 0
              ? t(
                  `${res.exported} toplantı aktarıldı, ${res.failed} toplantı aktarılamadı.`,
                  `Exported ${plural(res.exported, 'meeting', 'meetings')}; ${res.failed} failed.`
                )
              : t(`${res.exported} toplantı dışa aktarıldı.`, `Exported ${plural(res.exported, 'meeting', 'meetings')}.`)
          );
        }
      } catch (err) {
        onNotify?.('error', (err as Error).message || t('Dışa aktarım başarısız.', 'Export failed.'));
      } finally {
        setIsRunning(false);
      }
    },
    [onNotify]
  );

  const reveal = useCallback(() => {
    if (result) void window.zabit.revealFolder().catch(() => {});
  }, [result]);

  return { run, isRunning, progress, result, reveal };
}
